import React, {
  useRef,
  useState,
  DragEvent,
  useEffect,
  RefObject,
} from "react";
import { Box } from "@mui/material";

interface Props {
  placeholder?: string;
  multiple: boolean;
  onFileChanged: (files?: File[] | File | undefined) => void;
}

const FileUpload = (props: Props) => {
  const placeholder = props.placeholder ?? "Click or drop file here";
  const { onFileChanged, multiple } = props;
  const fileInput = useRef<HTMLInputElement>() as RefObject<HTMLInputElement>;
  const [dragOver, setDragOver] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  let filePrompt = <span>{placeholder}</span>;

  const assignFiles = (files?: File[]) => {
    if (files == null) {
      setFiles([]);
      onFileChanged(undefined);
    } else if (multiple) {
      setFiles(files);
      onFileChanged(files);
    } else {
      setFiles(files);
      onFileChanged(files[0]);
    }
  };

  const styleClasses = {
    fileUploadArea: {
      cursor: "pointer",

      "& input[type='file']": {
        position: "absolute",
        left: "-100vw",
        visibility: "hidden",
      },
    },
  };

  useEffect(() => {
    if (files && files.length > 0) {
      filePrompt = (
        <div>
          <span>File: {files[0].name}</span>
          <a
            href="#upload"
            onClick={(evt) => {
              evt.preventDefault();
              evt.stopPropagation();
              assignFiles(undefined);
            }}
          >
            Remove
          </a>
        </div>
      );
    }
  }, [files]);

  return (
    <Box p={1}>
      <Box
        sx={{
          border: "2px dashed var(--dmg-gray-300)",
          p: 2,
          my: 1,
          textAlign: "center",
          position: "relative",
          ...styleClasses.fileUploadArea,
        }}
        borderRadius={4}
        className={`${dragOver ? "drag-over" : ""}`}
        onClick={() => {
          if (fileInput && fileInput.current) {
            fileInput.current.click();
          }
        }}
        onDragOver={(evt: DragEvent<HTMLDivElement>) => {
          setDragOver(true);
          evt.preventDefault();
        }}
        onDragEnter={() => setDragOver(true)}
        onDragLeave={() => setDragOver(false)}
        onDrop={(evt: DragEvent<HTMLDivElement>) => {
          evt.preventDefault();
          setDragOver(false);
          assignFiles(Array.from(evt.dataTransfer.files));
        }}
      >
        {filePrompt}
        <input
          type="file"
          ref={fileInput}
          multiple={multiple}
          onChange={(changeEvent) => {
            const files = Array.from(changeEvent.target.files ?? []);
            changeEvent.target.value = "";
            assignFiles(files);
          }}
        />
      </Box>
    </Box>
  );
};

export default FileUpload;
